import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Chart from 'chart.js/auto';
import { BarChart3, Lock, PiggyBank, TrendingUp } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { LanguageLink } from '../components/LanguageLink';
import { useI18n } from '../i18n/i18n';
import { useSEO } from '../hooks/useSEO';

const ToolsPage = () => {
  const { t } = useI18n();

  // SEO Configuration
  const seoContent = useSEO('main', 'tools');

  const tool1ChartRef = useRef<HTMLCanvasElement | null>(null);
  const tool2ChartRef = useRef<HTMLCanvasElement | null>(null);
  const savingsChartRef = useRef<HTMLCanvasElement | null>(null);
  const mortgageChartRef = useRef<HTMLCanvasElement | null>(null);

  const chart1Ref = useRef<Chart | null>(null);
  const chart2Ref = useRef<Chart | null>(null);
  const savingsChartInstance = useRef<Chart | null>(null);
  const mortgageChartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    // Cleanup previous chart instances
    if (chart1Ref.current) {
      chart1Ref.current.destroy();
    }
    if (chart2Ref.current) {
      chart2Ref.current.destroy();
    }
    if (mortgageChartInstance.current) {
      mortgageChartInstance.current.destroy();
    }


    // Initialize preview charts
    if (tool1ChartRef.current) {
      const ctx = tool1ChartRef.current.getContext('2d');
      if (ctx) {
        chart1Ref.current = new Chart(ctx, {
          type: 'line',
          data: {
            labels: ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'],
            datasets: [{
              label: 'Growth',
              data: [1000, 2200, 3500, 5000, 6800],
              borderColor: '#EA580C',
              backgroundColor: 'rgba(234, 88, 12, 0.1)',
              tension: 0.3,
              fill: true
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false }
            },
            scales: {
              x: { display: false },
              y: { display: false }
            }
          }
        });
      }
    }

    if (tool2ChartRef.current) {
      const ctx = tool2ChartRef.current.getContext('2d');
      if (ctx) {
        chart2Ref.current = new Chart(ctx, {
          type: 'pie',
          data: {
            labels: [t('tools.salary.takeHomePay'), t('tools.salary.taxes'), t('tools.salary.deductions')],
            datasets: [{
              data: [70, 20, 10],
              backgroundColor: ['#EA580C', '#6B7280', '#D1D5DB']
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false }
            }
          }
        });
      }
    }

    // Initialize savings preview chart
    const savingsCanvas = document.getElementById('savings-preview-chart') as HTMLCanvasElement;
    if (savingsCanvas) {
      const ctx = savingsCanvas.getContext('2d');
      if (ctx) {
        savingsChartInstance.current = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: [t('tools.savings.needs'), t('tools.savings.wants'), t('tools.savings.savings')],
            datasets: [{
              data: [50, 30, 20],
              backgroundColor: ['#22C55E', '#3B82F6', '#A855F7'],
              borderWidth: 0,
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false }
            },
            scales: {
              x: { display: false },
              y: { display: false }
            }
          }
        });
      }
    }

    // Initialize mortgage preview chart
    if (mortgageChartRef.current) {
      const ctx = mortgageChartRef.current.getContext('2d');
      if (ctx) {
        mortgageChartInstance.current = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: ['Principal', 'Interest', 'Insurance', 'Taxes'],
            datasets: [{
              data: [65, 25, 6, 4],
              backgroundColor: ['#EA580C', '#FB923C', '#FED7AA', '#FFEDD5'],
              borderWidth: 0,
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false }
            },
            cutout: '60%'
          }
        });
      }
    }



    return () => {
      if (chart1Ref.current) chart1Ref.current.destroy();
      if (chart2Ref.current) chart2Ref.current.destroy();
      if (savingsChartInstance.current) savingsChartInstance.current.destroy();
      if (mortgageChartInstance.current) mortgageChartInstance.current.destroy();

    };
  }, [t]);

  return (
    <section className="bg-white py-16 fade-in">
      {seoContent}
      <div className="container mx-auto px-4 md:px-6">
        <h2 className="text-2xl md:text-3xl font-bold text-neutral-800 text-center mb-4">
          {t('tools.title')}
        </h2>
        <p className="text-neutral-600 text-center max-w-2xl mx-auto mb-12">
          {t('tools.subtitle')}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Tool 1 */}
          <Card className="bg-white rounded-xl border border-neutral-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
            <div className="aspect-video bg-neutral-100 flex items-center justify-center">
              <canvas id="tool1-preview-chart" ref={tool1ChartRef} width="300" height="150"></canvas>
            </div>
            <CardContent className="p-6">
              <h3 className="font-semibold text-xl mb-2">{t('tools.compound.title')}</h3>
              <p className="text-neutral-600 mb-4">{t('tools.compound.description')}</p>
              <LanguageLink to="/tools/compound-interest">
                <Button className="bg-[#EA580C] hover:bg-[#C2410C] text-white font-medium">
                  {t('home.tools.useTool')}
                </Button>
              </LanguageLink>
            </CardContent>
          </Card>

          {/* Tool 2 */}
          <Card className="bg-white rounded-xl border border-neutral-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
            <div className="aspect-video bg-neutral-100 flex items-center justify-center">
              <canvas id="tool2-preview-chart" ref={tool2ChartRef} width="300" height="150"></canvas>
            </div>
            <CardContent className="p-6">
              <h3 className="font-semibold text-xl mb-2">{t('tools.salary.title')}</h3>
              <p className="text-neutral-600 mb-4">{t('tools.salary.description')}</p>
              <LanguageLink to="/tools/net-salary">
                <Button className="bg-[#EA580C] hover:bg-[#C2410C] text-white font-medium">
                  {t('home.tools.useTool')}
                </Button>
              </LanguageLink>
            </CardContent>
          </Card>

           {/* Monthly Savings Calculator Tool */}
           <Card className="bg-white rounded-xl border border-neutral-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
            <div className="aspect-video bg-neutral-100 flex items-center justify-center relative">
              <canvas 
                id="savings-preview-chart"
                className="absolute inset-0 w-full h-full"
                style={{ opacity: 0.7 }}
              />
            </div>
            <CardContent className="p-6">
              <h3 className="font-semibold text-xl mb-2">{t('tools.savings.title')}</h3>
              <p className="text-neutral-600 mb-4">{t('tools.savings.description')}</p>
              <LanguageLink to="/tools/monthly-savings-calculator">
                <Button className="bg-[#EA580C] hover:bg-[#C2410C] text-white font-medium">
                  {t('home.tools.useTool')}
                </Button>
              </LanguageLink>
            </CardContent>
          </Card>

          {/* Mortgage Calculator (Premium) */}
          <Card className="bg-white rounded-xl border border-neutral-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden relative">
            <div className="absolute top-4 right-4 bg-[#EA580C] text-white text-xs px-2 py-1 rounded-full">
              {t('common.premium')}
            </div>
            <div className="aspect-video bg-neutral-100 flex items-center justify-center">
              <canvas id="mortgage-preview-chart" ref={mortgageChartRef} width="300" height="150"></canvas>
            </div>
            <CardContent className="p-6">
              <h3 className="font-semibold text-xl mb-2">{t('tools.mortgage.title')}</h3>
              <p className="text-neutral-600 mb-4">{t('tools.mortgage.description')}</p>
              <LanguageLink to="/tools/mortgage-calculator">
                <Button className="bg-[#EA580C] hover:bg-[#C2410C] text-white font-medium">
                  {t('home.tools.useTool')}
                </Button>
              </LanguageLink>
            </CardContent>
          </Card>

          {/* Salary Comparison Calculator */}
          <Card className="bg-white rounded-xl border border-neutral-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden relative">
            <div className="aspect-video bg-gradient-to-br from-[#FFF7ED] to-[#FFEDD5] flex items-center justify-center relative p-4">
              <div className="w-full h-full flex items-center justify-center">
                {/* Enhanced salary comparison visualization - MAXIMIZED WITHIN FRAME */}
                <div className="flex items-end justify-center gap-8 h-full w-full px-2">
                  <div className="flex flex-col items-center justify-end h-full">
                    <div className="bg-gradient-to-t from-[#DC2626] to-[#EF4444] rounded-t-lg shadow-xl mb-2" style={{width: '60px', height: '90px'}}></div>
                    <span className="text-xl text-[#DC2626] font-bold mb-1">€45k</span>
                    <span className="text-lg text-neutral-800 font-bold">Madrid</span>
                  </div>
                  <div className="flex flex-col items-center justify-end h-full">
                    <div className="bg-gradient-to-t from-[#EA580C] to-[#F97316] rounded-t-lg shadow-xl mb-2" style={{width: '60px', height: '120px'}}></div>
                    <span className="text-xl text-[#EA580C] font-bold mb-1">€65k</span>
                    <span className="text-lg text-neutral-800 font-bold">Berlin</span>
                  </div>
                  <div className="flex flex-col items-center justify-end h-full">
                    <div className="bg-gradient-to-t from-[#16A34A] to-[#22C55E] rounded-t-lg shadow-xl mb-2" style={{width: '60px', height: '150px'}}></div>
                    <span className="text-xl text-[#16A34A] font-bold mb-1">€85k</span>
                    <span className="text-lg text-neutral-800 font-bold">London</span>
                  </div>
                </div>
              </div>
              <div className="absolute top-4 left-4">
                <div className="w-10 h-10 bg-[#EA580C] rounded-full flex items-center justify-center shadow-sm">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
              </div>
            </div>
            <CardContent className="p-6">
              <h3 className="font-semibold text-xl mb-2">{t('tools.salaryComparison.title')}</h3>
              <p className="text-neutral-600 mb-4">{t('tools.salaryComparison.shortDesc')}</p>
              <LanguageLink to="/tools/salary-comparison">
                <Button className="bg-[#EA580C] hover:bg-[#C2410C] text-white font-medium">
                  {t('home.tools.useTool')}
                </Button>
              </LanguageLink>
            </CardContent>
          </Card>

          {/* Tool Card 4 (Future Tool - Premium) */}
          <Card className="bg-white rounded-xl border border-neutral-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden relative">
            <div className="absolute top-4 right-4 bg-neutral-800 text-white text-xs px-2 py-1 rounded-full">
              {t('common.premium')}
            </div>
            <div className="aspect-video bg-neutral-100 flex items-center justify-center">
              <Lock className="h-12 w-12 text-neutral-300" />
            </div>
            <CardContent className="p-6">
              <h3 className="font-semibold text-xl mb-2">{t('tools.portfolio.title')}</h3>
              <p className="text-neutral-600 mb-4">{t('tools.portfolio.description')}</p>
              <Button variant="secondary" className="bg-neutral-300 text-neutral-600 font-medium cursor-not-allowed">
                {t('common.comingSoon')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .fade-in {
            animation: fadeIn 0.5s ease forwards;
          }
        `}
      </style>
    </section>
  );
};

export default ToolsPage;